import { cn } from "@/lib/utils.ts";
import { addDays, addWeeks, format, isSameDay, startOfWeek } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";

interface DayWeekStripProps {
  /** First day in the currently-visible range (left column of the 2-day grid). */
  currentDate: Date;
  /** How many days are visible in the grid below — controls the pill width. */
  visibleDayCount: number;
  onDateChange: (date: Date) => void;
}

interface WeekRowProps {
  weekStart: Date;
  currentDate: Date;
  visibleDayCount: number;
  today: Date;
  onDateChange: (date: Date) => void;
}

/**
 * The day number's fill and text color across the four selected/today states.
 * Selection owns the box and today owns the color, so only the both-true case
 * combines them. Kept flat rather than nested: the repo forbids nested
 * ternaries, and the four states read better named than indented.
 */
const dayToneClass = (isSelected: boolean, isToday: boolean): string => {
  if (isSelected) {
    return isToday ? "bg-primary text-primary-foreground" : "bg-popover text-foreground";
  }
  return isToday ? "text-primary" : "text-foreground";
};

/**
 * One week's row inside the carousel — letters above, numbers below, following
 * native's CalendarStripDay model: selection is the box, today is the color.
 * The selected (viewed) day wears a rounded square — brand-filled when it is
 * also today, a white square otherwise — and an unselected today shows as a
 * primary-colored number. The 2-day view adds a `bg-muted` rounded-rectangle
 * tray across both visible days (corner radius 2px past the square's, staying
 * concentric); the 1-day view draws no tray at all. Identical content shape on
 * all three carousel slots; the non-center weeks naturally render no tray
 * because none of their days are in the visible range.
 */
const WeekRow = ({
  weekStart,
  currentDate,
  visibleDayCount,
  today,
  onDateChange,
}: WeekRowProps) => {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const visibleStartKey = format(currentDate, "yyyy-MM-dd");
  const visibleEndKey = format(addDays(currentDate, visibleDayCount - 1), "yyyy-MM-dd");

  return (
    <div className="shrink-0">
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const isTodayLetter = isSameDay(day, today);
          return (
            <div
              key={`letter-${day.toISOString()}`}
              className={cn(
                "text-[11px] text-center uppercase font-medium",
                isTodayLetter ? "text-primary" : "text-muted-foreground",
              )}
            >
              {format(day, "EEEEE")}
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-7 mt-1">
        {days.map((day) => {
          const dayKey = format(day, "yyyy-MM-dd");
          const inRange = dayKey >= visibleStartKey && dayKey <= visibleEndKey;
          const isFirstInRange = dayKey === visibleStartKey;
          const isLastInRange = dayKey === visibleEndKey;
          const isSelected = dayKey === visibleStartKey;
          const isToday = isSameDay(day, today);
          const showTray = visibleDayCount > 1;
          return (
            <button
              key={`num-${dayKey}`}
              type="button"
              onClick={() => onDateChange(day)}
              className="relative flex items-center justify-center h-10"
              aria-pressed={inRange}
              aria-label={format(day, "EEEE, MMMM d")}
            >
              {/* The 2-day tray, hugging the day squares with a uniform 5px
                  frame rather than filling the cells: each half runs from 20px
                  past its day's center (half the 30px square + the 5px frame)
                  to the shared cell edge, where a 1px overlap hides the seam.
                  inset-y-0 in the 40px cell gives the same 5px above and
                  below. Corners are 9px by eye (Frank's calibration), a touch
                  tighter than the concentric 12px the frame math suggests. */}
              {showTray && inRange && (
                <span
                  aria-hidden="true"
                  className={cn(
                    "absolute inset-y-0 bg-muted",
                    isFirstInRange ? "left-1/2 -ml-5 rounded-l-[9px]" : "left-0",
                    isLastInRange ? "right-1/2 -mr-5 rounded-r-[9px]" : "-right-px",
                  )}
                />
              )}
              <span
                className={cn(
                  // 30px box + 7px radius per Frank's calibration, on both the
                  // selected white square and the today-selected purple one.
                  "relative size-[30px] inline-flex items-center justify-center rounded-[7px] text-base font-semibold",
                  dayToneClass(isSelected, isToday),
                )}
              >
                {format(day, "d")}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * iOS-style week strip rendered above the day grid on mobile/tablet.
 *
 * Three side-by-side week strips in a horizontally-translatable row. Tap a day
 * to jump the grid to it; swipe left/right past ~50px to advance/retreat by
 * one week. Mirrors the main CalendarView carousel pattern but simpler — no
 * FullCalendar slot scroll sync, no long-press selection interaction. On
 * commit the row animates to the target, then snaps back to center with new
 * week-starts and fires `onDateChange(addWeeks(currentDate, ±1))` so the same
 * day-of-week in the adjacent week becomes selected. The grid carousel below
 * reacts to the resulting `currentDate` change via its own `[currentDate]`
 * effect.
 */
export const DayWeekStrip = ({ currentDate, visibleDayCount, onDateChange }: DayWeekStripProps) => {
  const today = new Date();

  // Three slot week-starts: prev / current / next. Center (index 1) is the
  // week containing `currentDate`. External `currentDate` changes re-center
  // via the effect below; local swipes update directly.
  const [slotWeekStarts, setSlotWeekStarts] = useState<[Date, Date, Date]>(() => {
    const center = startOfWeek(currentDate);
    return [addDays(center, -7), center, addDays(center, 7)];
  });
  const slotWeekStartsRef = useRef(slotWeekStarts);
  slotWeekStartsRef.current = slotWeekStarts;

  const outerRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const slotWidthRef = useRef(0);

  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchDirRef = useRef<"horizontal" | "vertical" | null>(null);
  const animTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks whether a swipe gesture is in flight (between touchstart and
  // animation commit). Blocks the external [currentDate] effect from
  // re-centering mid-swipe and racing with our commit transform.
  const isSwipingRef = useRef(false);

  const onDateChangeRef = useRef(onDateChange);
  onDateChangeRef.current = onDateChange;

  const applyLayout = useCallback(() => {
    const outer = outerRef.current;
    const row = rowRef.current;
    if (!outer || !row) return;
    const w = outer.clientWidth;
    if (w > 0) slotWidthRef.current = w;
    const slotWidth = slotWidthRef.current;
    if (!slotWidth) return;
    row.style.width = `${3 * slotWidth}px`;
    row.style.transform = `translateX(-${slotWidth}px)`;
    for (const child of Array.from(row.children) as HTMLElement[]) {
      child.style.width = `${slotWidth}px`;
    }
  }, []);

  useEffect(() => {
    applyLayout();
    const outer = outerRef.current;
    if (!outer) return;
    const observer = new ResizeObserver(() => applyLayout());
    observer.observe(outer);
    return () => observer.disconnect();
  }, [applyLayout]);

  // External `currentDate` change (e.g. main grid swipe, Today, tap on a
  // visible day): re-center slots around the new week if it isn't already
  // our center. Guarded by isSwipingRef so a commit that fires onDateChange
  // doesn't fight the transform we just set.
  useEffect(() => {
    if (isSwipingRef.current) return;
    const center = startOfWeek(currentDate);
    if (isSameDay(center, slotWeekStartsRef.current[1])) return;
    const newSlots: [Date, Date, Date] = [addDays(center, -7), center, addDays(center, 7)];
    slotWeekStartsRef.current = newSlots;
    setSlotWeekStarts(newSlots);
    const row = rowRef.current;
    if (row) {
      row.style.transition = "";
      row.style.transform = `translateX(-${slotWidthRef.current}px)`;
    }
  }, [currentDate]);

  useEffect(() => {
    return () => {
      if (animTimeoutRef.current) clearTimeout(animTimeoutRef.current);
    };
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchDirRef.current = null;
    isSwipingRef.current = true;
    if (animTimeoutRef.current) {
      clearTimeout(animTimeoutRef.current);
      animTimeoutRef.current = null;
    }
    const row = rowRef.current;
    if (row) {
      row.style.transition = "";
      row.style.transform = `translateX(-${slotWidthRef.current}px)`;
    }
  }, []);

  // Non-passive touchmove for live drag — bound to the outer element so we can
  // call preventDefault to suppress page scroll once direction is committed.
  useEffect(() => {
    const outer = outerRef.current;
    if (!outer) return;
    const onMove = (e: TouchEvent) => {
      const deltaX = e.touches[0].clientX - touchStartX.current;
      const deltaY = e.touches[0].clientY - touchStartY.current;
      if (!touchDirRef.current && (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)) {
        touchDirRef.current = Math.abs(deltaX) >= Math.abs(deltaY) ? "horizontal" : "vertical";
      }
      if (touchDirRef.current === "horizontal") {
        // Tap tolerance — small finger drift on a tap shouldn't suppress the
        // browser-synthesized click on the day buttons.
        if (Math.abs(deltaX) < 12) return;
        e.preventDefault();
        const row = rowRef.current;
        if (row) {
          row.style.transition = "";
          row.style.transform = `translateX(${-slotWidthRef.current + deltaX}px)`;
        }
      }
    };
    const onCancel = () => {
      isSwipingRef.current = false;
      touchDirRef.current = null;
      const row = rowRef.current;
      if (row) {
        row.style.transition = "transform 240ms ease";
        row.style.transform = `translateX(-${slotWidthRef.current}px)`;
      }
    };
    outer.addEventListener("touchmove", onMove, { passive: false });
    outer.addEventListener("touchcancel", onCancel);
    return () => {
      outer.removeEventListener("touchmove", onMove);
      outer.removeEventListener("touchcancel", onCancel);
    };
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchDirRef.current !== "horizontal") {
        isSwipingRef.current = false;
        return;
      }
      const row = rowRef.current;
      if (!row) return;

      const deltaX = e.changedTouches[0].clientX - touchStartX.current;
      const THRESHOLD = 50;
      const sw = slotWidthRef.current;

      if (Math.abs(deltaX) > THRESHOLD) {
        const goPrev = deltaX > 0;
        const targetTransform = goPrev ? "translateX(0px)" : `translateX(-${2 * sw}px)`;
        // addWeeks preserves time-of-day, so the resulting date keeps any
        // hour/minute carried by `currentDate` (the carousel hands us mid-day
        // Date instances). startOfWeek of the result gives the new center.
        const newCurrentDate = addWeeks(currentDate, goPrev ? -1 : 1);
        const newCenterWeek = startOfWeek(newCurrentDate);

        row.style.transition = "transform 240ms ease";
        row.style.transform = targetTransform;

        animTimeoutRef.current = setTimeout(() => {
          animTimeoutRef.current = null;
          const newSlots: [Date, Date, Date] = [
            addDays(newCenterWeek, -7),
            newCenterWeek,
            addDays(newCenterWeek, 7),
          ];
          slotWeekStartsRef.current = newSlots;
          setSlotWeekStarts(newSlots);
          row.style.transition = "";
          row.style.transform = `translateX(-${sw}px)`;
          isSwipingRef.current = false;
          onDateChangeRef.current(newCurrentDate);
        }, 240);
      } else {
        isSwipingRef.current = false;
        row.style.transition = "transform 240ms ease";
        row.style.transform = `translateX(-${sw}px)`;
      }
    },
    [currentDate],
  );

  return (
    <div
      ref={outerRef}
      className="bg-card border-b border-border pt-2 pb-1 select-none overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div ref={rowRef} className="flex">
        <WeekRow
          weekStart={slotWeekStarts[0]}
          currentDate={currentDate}
          visibleDayCount={visibleDayCount}
          today={today}
          onDateChange={onDateChange}
        />
        <WeekRow
          weekStart={slotWeekStarts[1]}
          currentDate={currentDate}
          visibleDayCount={visibleDayCount}
          today={today}
          onDateChange={onDateChange}
        />
        <WeekRow
          weekStart={slotWeekStarts[2]}
          currentDate={currentDate}
          visibleDayCount={visibleDayCount}
          today={today}
          onDateChange={onDateChange}
        />
      </div>
    </div>
  );
};
